import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ActionButton, ButtonModel } from '@notino/react-styleguide';
import { GetCatalogProductViewQuery } from '@notino/shared/definitions/types';

import { LoadingWrapper } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/Loading/LoadingWrapper';
import { ReviewsSkeleton } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/Loading/ReviewsSkeleton';
import { ReviewSummarySkeleton } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/Loading/ReviewSummarySkeleton';
import { messages } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/messages';
import { ReviewsItems } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/ReviewItems';
import { ReviewItemsProvider } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/ReviewItems/context';
import { SortBox } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/ReviewItems/styled';
import { ReviewsSummary } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/ReviewsSummary';
import { ErrorMessage } from '@containers/ProductDetailContainer/ProductDetail/ProductTabContent/Reviews/styled';
import { useProductDetailContext } from '@containers/ProductDetailContainer/ProductDetailContext';
import { CenterWrapper } from '@containers/ProductDetailContainer/styled';
import { withProviders } from '@context/contextConfig';
import { useLoggedInUserReviewFetch } from '@queries/useReviews/useLoggedInUserReviewFetch';
import { useReviewsFetch } from '@queries/useReviews/useReviewsFetch';
import { useReviewsSummaryFetch } from '@queries/useReviews/useReviewsSummaryFetch';

import { DEFAULT_REVIEW_COUNT } from '../constants';
import { headMetaMessages } from '../HeadMeta/messages';
import { ReviewsJsonLd } from '../ReviewsJsonLd';

import { ReviewCount, ReviewsWrapper, TitleWrapper } from './styled';

interface IReviewsComponentProps {
  productCode: string;
  product: GetCatalogProductViewQuery['productDetailByCatalogMasterId'];
  selectedVariant: GetCatalogProductViewQuery['productDetailByCatalogMasterId']['variants'][number];
  imageDomainPath: string;
  masterId: string;
  isAllReviewsPage?: boolean;
}

export const ReviewsComponent: FC<IReviewsComponentProps> = ({
  masterId,
  productCode,
  isAllReviewsPage,
  imageDomainPath,
  product,
  selectedVariant,
}) => {
  const {
    product: { catalogMasterId },
  } = useProductDetailContext();
  const {
    data: reviewsSummaryData,
    loading: reviewsSummaryLoading,
    error: reviewsSummaryError,
    refetch: reviewsSummaryRefetch,
  } = useReviewsSummaryFetch(catalogMasterId, { ssr: true });

  const {
    data,
    error,
    loading,
    refetch: reviewsRefetch,
    fetchMore,
  } = useReviewsFetch(DEFAULT_REVIEW_COUNT, productCode, {
    ssr: true,
  });

  const { data: loggedInUserReviewData, refetch: refetchLoggedInUserReview } =
    useLoggedInUserReviewFetch(productCode, { ssr: true });

  const { formatMessage } = useIntl();

  const productName = formatMessage(headMetaMessages.review, {
    brandName: product.brand.name,
    collection: `${product.collection}`,
  });

  const reviewOverview =
    reviewsSummaryData?.productDetailByCatalogMasterId?.reviewOverview;
  const reviewSummary =
    reviewsSummaryData?.productDetailByCatalogMasterId?.reviewSummary;

  const productReviewCount = reviewOverview?.count ?? 0;

  const reviewsLength = data?.reviews.length;

  const reviewCount = reviewsLength > 0 ? reviewsLength : '';

  return (
    <>
      <ReviewsJsonLd
        product={product}
        selectedVariant={selectedVariant}
        imageDomainPath={imageDomainPath}
        reviews={data}
      />

      <ReviewsWrapper>
        <TitleWrapper>
          {productName}
          <ReviewCount>{reviewCount}</ReviewCount>
        </TitleWrapper>

        <LoadingWrapper
          isLoading={reviewsSummaryLoading}
          isError={!reviewSummary || !!reviewsSummaryError}
          skeleton={<ReviewSummarySkeleton />}
          error={
            <CenterWrapper>
              <ErrorMessage>
                <FormattedMessage {...messages.reviewsSummaryFailed} />
              </ErrorMessage>

              <ActionButton
                onClick={() => reviewsSummaryRefetch()}
                buttonSize={ButtonModel.Sizes.small}
              >
                <FormattedMessage {...messages.tryAgain} />
              </ActionButton>
            </CenterWrapper>
          }
        >
          <ReviewsSummary
            reviewSummary={reviewSummary}
            loggedInUserReview={loggedInUserReviewData?.loggedInUserReview}
            productCode={productCode}
            masterId={masterId}
            catalogMasterId={catalogMasterId}
            isAllReviewsPage={isAllReviewsPage}
            refetchLoggedInUserReview={async () => {
              await refetchLoggedInUserReview({
                masterProductCode: productCode,
              });
            }}
          />
        </LoadingWrapper>

        <LoadingWrapper
          isLoading={loading}
          isError={!data || !!error}
          skeleton={<ReviewsSkeleton />}
          error={
            <>
              <SortBox />
              <CenterWrapper>
                <ErrorMessage>
                  <FormattedMessage {...messages.reviewsFailed} />
                </ErrorMessage>
                <ActionButton
                  onClick={() => reviewsRefetch()}
                  buttonSize={ButtonModel.Sizes.small}
                >
                  <FormattedMessage {...messages.tryAgain} />
                </ActionButton>
              </CenterWrapper>
            </>
          }
        />

        <ReviewsItems
          productCode={productCode}
          reviews={data?.reviews || []}
          reviewsLoading={loading}
          reviewsError={error}
          refetch={reviewsRefetch}
          fetchMore={fetchMore}
          productReviewCount={productReviewCount}
          isAllReviewsPage={isAllReviewsPage}
        />
      </ReviewsWrapper>
    </>
  );
};

export const Reviews =
  withProviders<IReviewsComponentProps>(ReviewItemsProvider)(ReviewsComponent);
